import { useCallback, useContext, useEffect, useState } from "react";

import { ViewStateContext } from "@is/contexts";
import { ScannerContext } from "@is/contexts/ScannerContext";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { ProductItem } from "@is/types";
import { convertFromStyleToProductItem } from "@is/utils";

import { useStyleDetailsEan } from "../content";
import { useContentItems } from "../useContentItems";

function useScanner(onScanSuccess?: () => void) {
  const [showStyleNotExistModal, setShowStyleNotExistModal] = useState(false);
  const { searchTerms, updateViewState } = useContext(ViewStateContext).content;

  const { scannedValue, scannedTime, resetScannedValue } =
    useContext(ScannerContext);

  const { data: styleDetails, error: styleDetailsNotFoundError } =
    useStyleDetailsEan(scannedValue);
  const { handleOpenProductDetails } = useContentItems();

  const handleCloseModal = useCallback(() => {
    resetScannedValue();
    setShowStyleNotExistModal(false);
  }, [resetScannedValue]);

  useEffect(() => {
    if (styleDetails) {
      if (showStyleNotExistModal) {
        setShowStyleNotExistModal(false);
      }

      updateViewState({
        animateLeavingEntryView: true,
        showSearchAndFooterDrawers: false,
      });

      const productItem: ProductItem = convertFromStyleToProductItem(
        styleDetails,
        scannedValue
      );

      handleOpenProductDetails({ productItem });

      trackEvent({
        eventType: TrackingEventTypes.BarCodeScanSuccess,
        data: {
          ean: scannedValue,
          variantId: productItem.id,
          styleId: productItem.styleId,
        },
      });

      if (typeof onScanSuccess === "function") {
        onScanSuccess();
      }
    } else if (styleDetailsNotFoundError) {
      setShowStyleNotExistModal(true);

      trackEvent({
        eventType: TrackingEventTypes.BarCodeScanFailed,
        data: {
          ean: scannedValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scannedValue,
    styleDetails,
    scannedTime,
    searchTerms,
    styleDetailsNotFoundError,
  ]);

  return {
    showStyleNotExistModal,
    handleCloseModal,
  };
}

export default useScanner;
